import React, { useState, useEffect } from "react";
import { Button, Form, Dropdown, Modal } from "react-bootstrap";
import { IoMdAddCircle } from "react-icons/io";
import { Link } from "react-router-dom";
import { MdEditNotifications } from "react-icons/md";
import { RiDeleteBin5Line } from "react-icons/ri";
import { IoNotificationsSharp } from "react-icons/io5";
import { FaEye } from "react-icons/fa";
import { BsChevronRight } from "react-icons/bs";
import "./announcements.css";
import AnnouncementLayout from "../../Components/AnnouncementLayout";

export default function EmpAnnouncements() {
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showSeenByModal, setShowSeenByModal] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // Search query state
  const [seenByUsers, setSeenByUsers] = useState([]);

  const BACKEND_URL = "https://backend.ems.hiradentallab.com";

  useEffect(() => {
    const fetchAnnouncements = async () => {
      setLoading(true);
      try {
        const url = searchQuery
          ? `${BACKEND_URL}/api/announcements/search?search=${searchQuery}`
          : `${BACKEND_URL}/api/announcements/announcements`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error("No announcement found");
        }
        const announcements = await response.json();
        setData(announcements);
        setCurrentPage(1);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, [searchQuery]);

  const handleDelete = async () => {
    if (!announcementToDelete) return;
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/announcements/announcements/${announcementToDelete._id}`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to delete announcement");
      }
      setData((prevData) =>
        prevData.filter((item) => item._id !== announcementToDelete._id)
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setShowDeleteModal(false);
      setAnnouncementToDelete(null);
    }
  };

  const openDeleteModal = (announcement) => {
    setAnnouncementToDelete(announcement);
    setShowDeleteModal(true);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= Math.ceil(data.length / rowsPerPage)) {
      setCurrentPage(pageNumber);
    }
  };

  const pageRange = () => {
    const pages = [];
    const totalPages = Math.ceil(data.length / rowsPerPage);
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
  };

  const handleViewAnnouncement = async (announcement) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/announcements/announcements/${announcement._id}`);
      if (!response.ok) {
        throw new Error("Failed to fetch announcement details");
      }
      const detailedAnnouncement = await response.json();
      setSelectedAnnouncement(detailedAnnouncement);
      setShowViewModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const openSeenByModal = (users) => {
    setSeenByUsers(users);
    setShowSeenByModal(true);
  };

  return (
    <AnnouncementLayout>
      <div className="container-fluid">
        <div>
          <h2 className="announcement-subtitle">
            <IoNotificationsSharp /> All Announcements
          </h2>
          <hr />
          <div className="announcement-container">
            <Link to="/add-announcements" style={{ textDecoration: "none" }}>
              <Button
                variant="primary"
                style={{ marginBottom: "15px", marginLeft: "-0.2%" }}
              >
                <IoMdAddCircle /> Add Announcements
              </Button>
            </Link>
            <div className="filter-container">
              <div className="filter-left">
                <Dropdown className="d-inline mx-0" autoClose="inside">
                  <Dropdown.Toggle id="dropdown-autoclose-inside">
                    Show Entries
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="custom-dropdown-menu">
                    <Dropdown.Item href="#">15</Dropdown.Item>
                    <Dropdown.Item href="#">25</Dropdown.Item>
                    <Dropdown.Item href="#">50</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <div className="filter-right">
                <Form className="d-flex">
                  <Form.Control
                    type="search"
                    placeholder="Search"
                    aria-label="Search"
                    className="search-bar"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </Form>
              </div>
            </div>
            <div className="table-responsive">
              {loading ? (
                <p>Loading...</p>
              ) : error ? (
                <p>Error: {error}</p>
              ) : (
                <table className="table table-striped table-style">
                  <thead className="announcement-table-header">
                    <tr className="table-header">
                      <th>SR</th>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Author</th>
                      <th>Created</th>
                      <th>Updated</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length > 0 ? (
                      data
                        .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                        .map((item, index) => (
                          <tr key={item._id} style={{ textAlign: "center" }}>
                            <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                            <td>{item.title}</td>
                            <td>{item.category}</td>
                            <td>{item.author}</td>
                            <td>{formatDate(item.createdAt)}</td>
                            <td>{formatDate(item.updatedAt)}</td>
                            <td>
                              <Button
                                style={{
                                  backgroundColor: "#007bff",
                                  color: "white",
                                  border: "none",
                                  marginRight: "5px",
                                }}
                                size="sm"
                                onClick={() => handleViewAnnouncement(item)}
                              >
                                <FaEye />
                              </Button>
                              <Link to={`/edit-announcement/${item._id}`}>
                                <Button variant="warning" size="sm">
                                  <MdEditNotifications />
                                </Button>
                              </Link>{" "}
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => openDeleteModal(item)}
                              >
                                <RiDeleteBin5Line />
                              </Button>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan="7">No data available</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="table-footer">
                    <tr className="announcement-table-footer">
                      <td>Total</td>
                      <td>-</td>
                      <td>{data.length}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              )}
              <div className="pagination-container">
                <div>
                  Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                  {Math.min(currentPage * rowsPerPage, data.length)} of{" "}
                  {data.length} entries
                </div>
                <nav>
                  <ul className="pagination">
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                    >
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage - 1);
                        }}
                      >
                        Previous
                      </a>
                    </li>
                    {pageRange().map((pageNumber) => (
                      <li
                        key={pageNumber}
                        className={`page-item ${
                          currentPage === pageNumber ? "active" : ""
                        }`}
                      >
                        <a
                          href="/#"
                          className="page-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(pageNumber);
                          }}
                        >
                          {pageNumber}
                        </a>
                      </li>
                    ))}
                    <li
                      className={`page-item ${
                        currentPage === Math.ceil(data.length / rowsPerPage)
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage + 1);
                        }}
                      >
                        Next
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton style={{ backgroundColor: "#007bff" }}>
            <Modal.Title style={{ color: "white" }}>Confirm Deletion</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: "#f5f5f5" }}>
            Are you sure you want to delete this announcement?
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: "#007bff" }}>
            <Button variant="light" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>

        {/* View Announcement Modal */}
        <Modal
          show={showViewModal}
          onHide={() => setShowViewModal(false)}
          centered
          size="lg"
          dialogClassName="fancy-modal"
        >
          <Modal.Header closeButton style={{ backgroundColor: "#007bff" }}>
            <Modal.Title style={{ color: "white" }}>{selectedAnnouncement?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="fancy-modal-body">
            {selectedAnnouncement && (
              <>
                <h5 className="modal-title">{selectedAnnouncement.title}</h5>
                <p className="modal-text">{selectedAnnouncement.content}</p>
                <p className="modal-detail">
                  <strong>Category:</strong> {selectedAnnouncement.category}
                </p>
                <p className="modal-detail">
                  <strong>Author:</strong>{" "}
                  {selectedAnnouncement.author || "Admin"}
                </p>
                <p className="modal-detail">
                  <strong>Created:</strong>{" "}
                  {formatDate(selectedAnnouncement.createdAt)}
                </p>
                <p className="modal-detail">
                  <strong>Updated:</strong>{" "}
                  {formatDate(selectedAnnouncement.updatedAt)}
                </p>
                {selectedAnnouncement.seenBy && selectedAnnouncement.seenBy.length > 0 && (
                  <div className="modal-section">
                    <strong>Seen By:</strong>
                    <ul className="modal-list">
                      <li className="modal-list-item" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        {selectedAnnouncement.seenBy[0].user?.name || `Unknown User (ID: ${selectedAnnouncement.seenBy[0].user?._id || selectedAnnouncement.seenBy[0].userId})`} at {formatDateTime(selectedAnnouncement.seenBy[0].seenAt)}
                        {selectedAnnouncement.seenBy.length > 1 && (
                          <BsChevronRight
                            style={{ color: "#007bff", cursor: "pointer", fontSize: "1.2rem" }}
                            onClick={() => openSeenByModal(selectedAnnouncement.seenBy)}
                          />
                        )}
                      </li>
                    </ul>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: "#007bff" }}>
            <Button variant="light" onClick={() => setShowViewModal(false)} className="modal-close-btn">
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Seen By Details Modal */}
        <Modal
          show={showSeenByModal}
          onHide={() => setShowSeenByModal(false)}
          dialogClassName="fancy-modal-right"
        >
          <Modal.Header closeButton style={{ backgroundColor: "#007bff" }}>
            <Modal.Title style={{ color: "white" }}>Users Who Have Seen</Modal.Title>
          </Modal.Header>
          <Modal.Body className="fancy-modal-body">
            <ul className="modal-list">
              {seenByUsers.map((user, index) => (
                <li key={index} className="modal-list-item" style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                  <strong>{user.user?.name || `Unknown User (ID: ${user.user?._id || user.userId})`}</strong> - Seen at {formatDateTime(user.seenAt)}
                </li>
              ))}
            </ul>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: "#007bff" }}>
            <Button variant="light" onClick={() => setShowSeenByModal(false)} className="modal-close-btn">
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </AnnouncementLayout>
  );
}